import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../redux/store';
import { apiPut } from '../../utils/api';
import { showError, showSuccess } from '../../utils/alert';
import InputField from '../form/InputField';
import { cities } from '../../data/cities';
import LoadingButton from '../form/LoadingButton';
import SubmitButton from '../form/SubmitButton';

interface OwnerFormData {
  name: string;
  email: string;
  city: string;
}

interface OwnerFormProps {
  /** where to navigate after successful submit */
  successRedirect: string;
  /** text of the submit button */
  submitLabel: string;
  /** success message translation key or string */
  successMessage?: string;
  
  defaultValues?:OwnerFormData
}

export default function OwnerForm({
  successRedirect,
  submitLabel,
  successMessage,
  defaultValues
}: OwnerFormProps) {
  const { t } = useTranslation();
  const {accessToken} = useSelector((s: RootState) => s.owner);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { handleSubmit, register, formState: { errors } } = useForm<OwnerFormData>({
    defaultValues:{
        email:defaultValues?.email,
        name:defaultValues?.name,
        city:defaultValues?.city
    }
  });

  const handleRegister = async (data: OwnerFormData) => {
    try {
      setLoading(true);
      await apiPut('owner', data, { Authorization: accessToken });
      showSuccess(successMessage ?? t("owner.registerSuccess"));
      if(successMessage){
        navigate(successRedirect);
      }
    } catch (err: any) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleRegister)} className="flex flex-col gap-4">
      <InputField
        placeholder={t('owner.namePlaceholder')}
        register={register('name', { required: t('owner.nameRequired') })}
        error={errors.name}
      />

      <InputField
        placeholder={t('owner.emailPlaceholder')}
        register={register('email', { 
          required: t('owner.emailRequired'),
          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('owner.emailInvalid') }
        })}
        error={errors.email}
        type="email"
      />

      <div>
        <select
          {...register("city", { required: t("required_field") })}
          className="border border-LightGrey3 rounded p-3 mt-2 w-full focus:outline-none focus:ring-2 focus:ring-primary placeholder:text-sm"
        >
          <option value="">{t('selectCity')}</option>
          {cities.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {errors.city && <span className="text-red-500">{errors.city.message}</span>}
      </div>

      {loading
        ? <LoadingButton height={64} />
        : <SubmitButton height={64} label={submitLabel} />
      }
    </form>
  );
}
